import { flow, isEmpty } from 'lodash';

import {
  AxisRangeErrorReturn,
  flattenUndefinedRange,
  isAxisRangeValid,
} from '@neptune/shared/widgets-domain';

import { ChartWidgetOptions } from 'domain/widget';

export const correctChartXAxisOptions = flow([
  forceStepWhenAveraging,
  forceLinearScaleWhenRequired,
  normalizeMetricsStepsRange,
  onlyAllowXAxisMetricForTimeSeries,
  onlyAllowXAxisRangeForNumericMode,
  onlyAllowCustomXExpressionWithValues,
  forceCustomExpressionSeriesModeWhenCustomXExpression,
]);

function onlyAllowXAxisMetricForTimeSeries(options: ChartWidgetOptions): ChartWidgetOptions {
  if (options.xAxisMode === 'timeSeries') {
    return options;
  }

  return {
    ...options,
    xAxisMetric: undefined,
  };
}

function normalizeMetricsStepsRange(options: ChartWidgetOptions): ChartWidgetOptions {
  return {
    ...options,
    metricsStepsRange: flattenUndefinedRange(options.metricsStepsRange),
  };
}

function onlyAllowXAxisRangeForNumericMode(options: ChartWidgetOptions): ChartWidgetOptions {
  if (
    !options.xAxisRange ||
    (options.xAxisMode !== 'relativeTime' && options.xAxisMode !== 'absoluteTime')
  ) {
    return options;
  }

  return {
    ...options,
    xAxisRange: undefined,
  };
}

function forceLinearScaleWhenRequired(options: ChartWidgetOptions): ChartWidgetOptions {
  if (!requiresLinearScaleOnX(options.xAxisMode, options.averaging)) {
    return options;
  }

  return {
    ...options,
    xAxisScale: 'linear',
  };
}

function requiresLinearScaleOnX(xAxisMode: string | undefined, isAveragingActive?: boolean) {
  return (xAxisMode === 'relativeTime' || xAxisMode === 'absoluteTime') && !isAveragingActive;
}

function forceStepWhenAveraging(options: ChartWidgetOptions): ChartWidgetOptions {
  if (!options.averaging) {
    return options;
  }

  return {
    ...options,
    xAxisMode: 'step',
  };
}

const ALLOW_CUSTOM_X_EXPRESSION_WITHOUT_NAME = true;

function onlyAllowCustomXExpressionWithValues(options: ChartWidgetOptions): ChartWidgetOptions {
  const hasExpression = !isEmpty(options.customXExpression);
  const hasName = !isEmpty(options.customXExpressionName);
  const shouldKeepCustomX = hasExpression && (hasName || ALLOW_CUSTOM_X_EXPRESSION_WITHOUT_NAME);

  if (shouldKeepCustomX) {
    return options;
  }

  return {
    ...options,
    customXExpression: undefined,
    customXExpressionName: undefined,
  };
}

function forceCustomExpressionSeriesModeWhenCustomXExpression(
  options: ChartWidgetOptions,
): ChartWidgetOptions {
  if (!options.customXExpression) {
    return options;
  }

  return { ...options, xAxisMode: 'customExpressionSeries', xAxisMetric: undefined };
}

export function isMetricsStepsRangeValid(
  metricsStepsRange: ChartWidgetOptions['metricsStepsRange'],
): AxisRangeErrorReturn {
  if (isMinHigherThanOrEqualMax(metricsStepsRange?.[0], metricsStepsRange?.[1])) {
    return {
      valid: false,
      errorMessage: 'Range maximum value must be greater than the minimum value.',
      errorType: 'values',
    };
  }

  if (
    !isValidXAxisRangeValue(metricsStepsRange?.[0]) ||
    !isValidXAxisRangeValue(metricsStepsRange?.[1])
  ) {
    return {
      valid: false,
      errorMessage: 'The step range can contain only non-negative values.',
      errorType: 'values',
    };
  }

  return {
    valid: true,
  };
}

const isValidXAxisRangeValue = (number: number | undefined | null) => {
  if (number === undefined || number === null) {
    return true;
  }

  return number >= 0;
};

const isMinHigherThanOrEqualMax = (min: number | undefined, max: number | undefined) => {
  if (min === undefined || min === null || max === undefined || max === null) {
    return false;
  }

  return max <= min;
};

export function correctChartYAxisOptions<Options extends ChartWidgetOptions>(
  options: Options,
): Options {
  const validateObject = isAxisRangeValid(options.yAxisRange, options.yAxisScale);

  const yAxisScale =
    !validateObject.valid && validateObject.errorType === 'scale' ? 'linear' : options.yAxisScale;

  return {
    ...options,
    yAxisRange: flattenUndefinedRange(options.yAxisRange),
    yAxisScale,
  };
}

export const correctChartOptions: <Options extends ChartWidgetOptions>(
  options: Options,
) => Options = flow([correctChartXAxisOptions, correctChartYAxisOptions]);
